import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { useRef, useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { useLocation, useNavigate } from "react-router-dom"

import { MentionInput } from "./MentionInput"
import { PostToolbar } from "./post/PostToolbar"
import { ImageUploader } from "./post/ImageUploader"
import { PollCreator, PollOption } from "./post/PollCreator"
import { GifPicker } from "./post/GifPicker"
import { X } from "lucide-react"

interface CreatePostProps {
  onPostCreated?: () => void
  onPostSuccess?: () => Promise<void>
  placeholder?: string
}

export function CreatePost({ onPostCreated, onPostSuccess, placeholder }: CreatePostProps) {
  const [content, setContent] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [images, setImages] = useState<File[]>([])
  const [pollOptions, setPollOptions] = useState<PollOption[] | null>(null)
  const [pollQuestion, setPollQuestion] = useState("")
  const [gifUrl, setGifUrl] = useState<string | null>(null)
  const [gifPickerOpen, setGifPickerOpen] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()
  const location = useLocation()
  const navigate = useNavigate()
  

  const getPostType = () => {
    if (location.pathname.includes('feed')) return 'feed'
    if (location.pathname.includes('introductions')) return 'introduction'
    if (location.pathname.includes('learning')) return 'learning'
    if (location.pathname.includes('questions')) return 'question'
    
    return 'introduction'
  }

  const uploadImages = async (userId: string): Promise<string[]> => {
    const urls: string[] = []
    for (const file of images) {
      const ext = file.name.split('.').pop()
      const path = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
      const { error } = await supabase.storage
        .from('community-images')
        .upload(path, file)
      if (error) throw error
      const { data: { publicUrl } } = supabase.storage
        .from('community-images')
        .getPublicUrl(path)
      urls.push(publicUrl)
    }
    return urls
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!content.trim() || content.length > 500) return

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      toast({ title: "Erro", description: "Você precisa estar logado para publicar.", variant: "destructive" })
      navigate("/login")
      return
    }

    // Validate poll
    if (pollOptions) {
      const filledOptions = pollOptions.filter(o => o.text.trim())
      if (filledOptions.length < 2) {
        toast({ title: "Enquete incompleta", description: "Preencha pelo menos 2 opções.", variant: "destructive" })
        return
      }
    }

    setIsSubmitting(true)
    try {
      // Upload images if any
      let imageUrls: string[] = []
      if (images.length > 0) {
        imageUrls = await uploadImages(user.id)
      }

      // Create post
      const { data: post, error } = await supabase
        .from('community_posts')
        .insert({
          content: content.trim(),
          post_type: getPostType(),
          author_id: user.id,
          image_urls: imageUrls.length > 0 ? imageUrls : undefined,
          gif_url: gifUrl || undefined,
        } as any)
        .select('id')
        .single()

      if (error) throw error

      // Create poll if exists
      if (pollOptions && post) {
        const filledOptions = pollOptions.filter(o => o.text.trim())
        const { data: poll, error: pollError } = await supabase
          .from('community_polls')
          .insert({ post_id: post.id, question: pollQuestion.trim() || null } as any)
          .select('id')
          .single()

        if (pollError) throw pollError

        if (poll) {
          const optionsToInsert = filledOptions.map(o => ({
            poll_id: poll.id,
            option_text: o.text.trim()
          }))
          const { error: optError } = await supabase
            .from('community_poll_options')
            .insert(optionsToInsert as any)

          if (optError) throw optError
        }
      }

      
      toast({ title: "Post criado com sucesso!", description: "Seu post foi publicado na comunidade." })

      // Reset state
      setContent("")
      setImages([])
      setPollOptions(null)
      setPollQuestion("")
      setGifUrl(null)

      if (onPostCreated) onPostCreated()
      if (onPostSuccess) await onPostSuccess()
    } catch (error) {
      console.error('Error creating post:', error)
      let errMsg = "Tente novamente."
      if (error && typeof error === 'object' && 'message' in error) {
        const msg = (error as any).message || ""
        if (msg.includes('post_type_check') || msg.includes('check constraint')) {
          errMsg = "Tipo de post não suportado. Atualize a página e tente novamente."
        } else {
          errMsg = msg
        }
      }
      toast({ title: "Erro ao criar post", description: errMsg, variant: "destructive" })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleImageClick = () => {
    if (images.length === 0) {
      imageInputRef.current?.click()
    }
  }

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const valid = files.filter(f => f.type.startsWith('image/') && f.size <= 2 * 1024 * 1024)
    setImages(prev => [...prev, ...valid].slice(0, 4))
    if (imageInputRef.current) imageInputRef.current.value = ''
  }

  return (
    <Card className="mb-6">
      <form onSubmit={handleSubmit}>
        <CardContent className="pt-6 space-y-3">
          <MentionInput
            placeholder={placeholder || "Compartilhe algo com a comunidade... Use @ para mencionar alguém"}
            value={content}
            onChange={setContent}
            disabled={isSubmitting}
            minHeight="100px"
          />

          {/* Image previews */}
          <ImageUploader images={images} onImagesChange={setImages} disabled={isSubmitting} />

          {/* GIF preview */}
          {gifUrl && (
            <div className="relative inline-block">
              <img src={gifUrl} alt="GIF" className="rounded-lg max-h-48" />
              <Button
                type="button" variant="destructive" size="sm"
                onClick={() => setGifUrl(null)}
                className="absolute top-1 right-1 h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}

          {/* Poll creator */}
          {pollOptions && (
            <PollCreator
              question={pollQuestion}
              onQuestionChange={setPollQuestion}
              options={pollOptions}
              onOptionsChange={setPollOptions}
              onRemove={() => { setPollOptions(null); setPollQuestion(""); }}
              disabled={isSubmitting}
            />
          )}

          {/* Toolbar */}
          <PostToolbar
            onImageClick={handleImageClick}
            onLinkClick={() => {}}
            onPollClick={() => setPollOptions([{ text: '' }, { text: '' }])}
            onGifClick={() => setGifPickerOpen(true)}
            hasImages={images.length > 0}
            hasPoll={!!pollOptions}
            hasGif={!!gifUrl}
            disabled={isSubmitting}
          />
        </CardContent>
        <CardFooter className="flex justify-between items-center">
          <p className={`text-sm ${content.length > 500 ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
            {content.length}/500 caracteres
          </p>
          <Button
            type="submit"
            disabled={!content.trim() || content.length > 500 || isSubmitting}
            className="bg-primary hover:bg-primary/90"
          >
            {isSubmitting ? "Publicando..." : "Publicar"}
          </Button>
        </CardFooter>
      </form>

      {/* Hidden file input */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleImageFileChange}
        className="hidden"
      />

      {/* GIF Picker Dialog */}
      <GifPicker
        open={gifPickerOpen}
        onOpenChange={setGifPickerOpen}
        onSelect={(url) => setGifUrl(url)}
      />
    </Card>
  )
}
